import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiArrowRight,
  FiShoppingBag,
  FiShield,
  FiTruck,
  FiCheckCircle,
} from "react-icons/fi";
import "./signup.css";
import Footer from "../../components/footer/footer";
import Header from "../../components/header/header";
const REACT_APP_API_URL = process.env.REACT_APP_API_URL ;
const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [focusedField, setFocusedField] = useState(null);

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (password !== confirmPassword) {
    alert("Passwords do not match!");
    return;
  }

  try {
    const guestId = localStorage.getItem("guestId");

    const response = await fetch(`${REACT_APP_API_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name,
        email,
        password,
        guestId, // send guestId for cart merge
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      alert(data.message || "Signup failed");
      return;
    }

    // ✅ Save JWT token
    localStorage.setItem("token", data.token);

    // ✅ Remove guestId after successful signup
    localStorage.removeItem("guestId");

    alert("Account created successfully!");

    // Redirect to home
   window.location.href = "/login";


  } catch (error) {
    console.error("Signup error:", error);
    alert("Something went wrong");
  }
};


  // Password strength indicator
  const getPasswordStrength = () => {
    if (!password) return { level: 0, label: "", color: "" };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    const levels = [
      { level: 1, label: "Weak", color: "#ef4444" },
      { level: 2, label: "Fair", color: "#f59e0b" },
      { level: 3, label: "Good", color: "#06b6d4" },
      { level: 4, label: "Strong", color: "#10b981" },
    ];
    return levels[strength - 1] || { level: 0, label: "", color: "" };
  };

  const passwordStrength = getPasswordStrength();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.07,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const brandingVariants = {
    hidden: { opacity: 0, x: -40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.12,
      },
    },
  };

  const brandItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -15 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: 0.5 + i * 0.15, duration: 0.4, ease: "easeOut" },
    }),
  };

  return (
    <div>
      {/* Header */}
      <Header />

      <div className="signup-page">
        {/* Floating Background Shapes */}
        <div className="signup-floating-shapes">
          <div className="signup-float-shape s-shape-1"></div>
          <div className="signup-float-shape s-shape-2"></div>
          <div className="signup-float-shape s-shape-3"></div>
          <div className="signup-float-shape s-shape-4"></div>
          <div className="signup-float-shape s-shape-5"></div>
          <div className="signup-float-shape s-shape-6"></div>
        </div>

        <motion.div
          className="signup-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* ── Left Branding Panel ── */}
          <motion.div
            className="signup-branding"
            variants={brandingVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="signup-branding-content">
              <motion.div
                className="signup-branding-icon"
                variants={brandItemVariants}
              >
                ✨
              </motion.div>
              <motion.h1
                className="signup-branding-title"
                variants={brandItemVariants}
              >
                Join Crescent
              </motion.h1>
              <motion.p
                className="signup-branding-subtitle"
                variants={brandItemVariants}
              >
                Create your account and unlock a world of premium shopping
                experiences.
              </motion.p>

              <div className="signup-branding-features">
                {[
                  { icon: <FiShoppingBag />, text: "Exclusive Deals" },
                  { icon: <FiShield />, text: "Secure & Private" },
                  { icon: <FiTruck />, text: "Free Shipping" },
                  { icon: <FiCheckCircle />, text: "Easy Returns" },
                ].map((feature, i) => (
                  <motion.div
                    className="signup-branding-feature"
                    key={i}
                    custom={i}
                    variants={featureVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <span className="signup-feature-icon">{feature.icon}</span>
                    <span>{feature.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ── Right Form Panel ── */}
          <motion.div
            className="signup-form-panel"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div className="signup-form-header" variants={itemVariants}>
              <h2>Create Account</h2>
              <p>Fill in your details to get started</p>
            </motion.div>

            {/* Form */}
            <form className="signup-form" onSubmit={handleSubmit}>
              {/* Name */}
              <motion.div
                className="input-group-signup"
                variants={itemVariants}
              >
                <label htmlFor="signup-name">Full Name</label>
                <div className="signup-input-wrapper">
                  <motion.span
                    className="signup-input-icon"
                    animate={{
                      color: focusedField === "name" ? "#6366f1" : "#9ca3af",
                      scale: focusedField === "name" ? 1.15 : 1,
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiUser />
                  </motion.span>
                  <input
                    id="signup-name"
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={() => setFocusedField("name")}
                    onBlur={() => setFocusedField(null)}
                    required
                  />
                </div>
              </motion.div>

              {/* Email */}
              <motion.div
                className="input-group-signup"
                variants={itemVariants}
              >
                <label htmlFor="signup-email">Email Address</label>
                <div className="signup-input-wrapper">
                  <motion.span
                    className="signup-input-icon"
                    animate={{
                      color: focusedField === "email" ? "#6366f1" : "#9ca3af",
                      scale: focusedField === "email" ? 1.15 : 1,
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiMail />
                  </motion.span>
                  <input
                    id="signup-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField("email")}
                    onBlur={() => setFocusedField(null)}
                    required
                  />
                </div>
              </motion.div>

              {/* Password */}
              <motion.div
                className="input-group-signup"
                variants={itemVariants}
              >
                <label htmlFor="signup-password">Password</label>
                <div className="signup-input-wrapper">
                  <motion.span
                    className="signup-input-icon"
                    animate={{
                      color:
                        focusedField === "password" ? "#6366f1" : "#9ca3af",
                      scale: focusedField === "password" ? 1.15 : 1,
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiLock />
                  </motion.span>
                  <input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField("password")}
                    onBlur={() => setFocusedField(null)}
                    required
                  />
                  <button
                    type="button"
                    className="signup-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={showPassword ? "visible" : "hidden"}
                        initial={{ opacity: 0, rotateY: -90 }}
                        animate={{ opacity: 1, rotateY: 0 }}
                        exit={{ opacity: 0, rotateY: 90 }}
                        transition={{ duration: 0.2 }}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </motion.span>
                    </AnimatePresence>
                  </button>
                </div>
                {/* Password Strength Bar */}
                {password && (
                  <motion.div
                    className="password-strength"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="strength-bars">
                      {[1, 2, 3, 4].map((i) => (
                        <motion.div
                          key={i}
                          className="strength-bar"
                          initial={{ scaleX: 0 }}
                          animate={{
                            scaleX: i <= passwordStrength.level ? 1 : 0.3,
                            backgroundColor:
                              i <= passwordStrength.level
                                ? passwordStrength.color
                                : "#e5e7eb",
                          }}
                          transition={{ duration: 0.3, delay: i * 0.05 }}
                        />
                      ))}
                    </div>
                    <span
                      className="strength-label"
                      style={{ color: passwordStrength.color }}
                    >
                      {passwordStrength.label}
                    </span>
                  </motion.div>
                )}
              </motion.div>

              {/* Confirm Password */}
              <motion.div
                className="input-group-signup"
                variants={itemVariants}
              >
                <label htmlFor="signup-confirm-password">
                  Confirm Password
                </label>
                <div className="signup-input-wrapper">
                  <motion.span
                    className="signup-input-icon"
                    animate={{
                      color:
                        focusedField === "confirmPassword"
                          ? "#6366f1"
                          : "#9ca3af",
                      scale: focusedField === "confirmPassword" ? 1.15 : 1,
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiLock />
                  </motion.span>
                  <input
                    id="signup-confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocusedField("confirmPassword")}
                    onBlur={() => setFocusedField(null)}
                    required
                  />
                  <button
                    type="button"
                    className="signup-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label="Toggle confirm password visibility"
                  >
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={showConfirmPassword ? "visible" : "hidden"}
                        initial={{ opacity: 0, rotateY: -90 }}
                        animate={{ opacity: 1, rotateY: 0 }}
                        exit={{ opacity: 0, rotateY: 90 }}
                        transition={{ duration: 0.2 }}
                      >
                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                      </motion.span>
                    </AnimatePresence>
                  </button>
                </div>
                {/* Password match indicator */}
                {confirmPassword && (
                  <motion.div
                    className="password-match"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {password === confirmPassword ? (
                      <span className="match-success">
                        <FiCheckCircle /> Passwords match
                      </span>
                    ) : (
                      <span className="match-error">
                        Passwords do not match
                      </span>
                    )}
                  </motion.div>
                )}
              </motion.div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                className="signup-submit-btn"
                variants={itemVariants}
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="signup-btn-content">
                  Create Account
                  <motion.span
                    animate={{ x: [0, 4, 0] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <FiArrowRight />
                  </motion.span>
                </span>
              </motion.button>
            </form>

            {/* Login Link */}
            <motion.p className="signup-login-link" variants={itemVariants}>
              Already have an account?
              <Link to="/login" className="login-link">
                Sign In
              </Link>
            </motion.p>
          </motion.div>
        </motion.div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Signup;
